import * as z from "zod";
import { isoDate } from "./common";

/**
 * The marketing back-office: the contact list, the templates it sends, and the
 * campaigns built from them.
 *
 * A campaign freezes both its body and its recipient list at creation, so a
 * template edited afterwards changes nothing that is already queued.
 */

export const MARKETING_CONTACT_SOURCES = ["FIREBASE", "CSV", "MANUAL"] as const;
export const MARKETING_TEMPLATE_KINDS = ["MARKETING", "SHOP_CLAIM", "TAP_TO_PAY_LAUNCH"] as const;
export const MARKETING_CAMPAIGN_STATUSES = ["DRAFT", "SENDING", "PAUSED", "COMPLETED", "CANCELED"] as const;

export const MarketingContact = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  source: z.enum(MARKETING_CONTACT_SOURCES),
  /** Never cleared by an import. Only an admin toggle re-subscribes an address. */
  unsubscribed: z.boolean(),
  createdAt: isoDate,
});
export type MarketingContact = z.output<typeof MarketingContact>;

export const MarketingContactList = z.object({
  contacts: z.array(MarketingContact),
  /** Totals cover the whole list, not the returned page. */
  total: z.number(),
  /** How many a campaign built now would reach. */
  subscribed: z.number(),
  unsubscribed: z.number(),
});
export type MarketingContactList = z.output<typeof MarketingContactList>;

export const MarketingImportResult = z.object({
  totalRows: z.number(),
  created: z.number(),
  /** Addresses already on the list. Their opt-out is preserved. */
  updated: z.number(),
  duplicates: z.number(),
  invalid: z.number(),
  invalidSamples: z.array(z.string()),
});
export type MarketingImportResult = z.output<typeof MarketingImportResult>;

export const MarketingFirebaseSync = z.object({
  scanned: z.number(),
  created: z.number(),
  updated: z.number(),
  /** Identities with no email — anonymous storefront sign-ins are most of them. */
  skipped: z.number(),
});
export type MarketingFirebaseSync = z.output<typeof MarketingFirebaseSync>;

export const MarketingTemplate = z.object({
  id: z.string(),
  /** SHOP_CLAIM and TAP_TO_PAY_LAUNCH are transactional and excluded from campaigns. */
  kind: z.enum(MARKETING_TEMPLATE_KINDS),
  name: z.string(),
  subject: z.string(),
  html: z.string(),
  /** Derived from the HTML when null. */
  text: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type MarketingTemplate = z.output<typeof MarketingTemplate>;

export const MarketingCampaign = z.object({
  id: z.string(),
  name: z.string(),
  templateId: z.string().nullable(),
  status: z.enum(MARKETING_CAMPAIGN_STATUSES),
  /** Copied from the template when the campaign was created. */
  subject: z.string(),
  /** Contacts subscribed at the moment the campaign was built. */
  totalRecipients: z.number(),
  sentCount: z.number(),
  failedCount: z.number(),
  startedAt: isoDate.nullable(),
  completedAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type MarketingCampaign = z.output<typeof MarketingCampaign>;

export const MarketingDeliveryFailure = z.object({
  email: z.string(),
  error: z.string().nullable(),
});
export type MarketingDeliveryFailure = z.output<typeof MarketingDeliveryFailure>;

export const MarketingCampaignDetail = z.object({
  campaign: MarketingCampaign,
  /** The body as it was frozen at creation. */
  html: z.string(),
  pending: z.number(),
  /** Unsubscribed after the campaign was built, or written off when it was canceled. */
  skipped: z.number(),
  recentFailures: z.array(MarketingDeliveryFailure),
});
export type MarketingCampaignDetail = z.output<typeof MarketingCampaignDetail>;

export interface MarketingContactQuery {
  /** Matches email or name, case-insensitive. */
  search?: string;
  status?: "all" | "subscribed" | "unsubscribed";
  limit?: number;
  offset?: number;
}

export interface NewMarketingTemplate {
  name: string;
  subject: string;
  /** Marketing placeholders are `{{name}}`, `{{email}}` and `{{unsubscribe_url}}`. */
  html: string;
  text?: string;
}

export interface MarketingTemplatePatch {
  name?: string;
  subject?: string;
  html?: string;
  text?: string;
}
